import { HotkeyDecorator } from "@/components/atoms/HotkeyDecorator";
import { SimpleEnum } from "@/components/molecules/SimpleEnum";
import { Dialog } from "@/components/organisms/Dialog";
import { Queries } from "@/constants";
import { useBackendQuery } from "@/hooks/useBackendQuery";
import { Utils } from "@/utils";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";

import { categoryEnumColor } from "./TriageReconcileRow";

/** An account a suggestion would post the payment to, looked over first. */
export type TriagePostedAccount = { identifier: string; name: string };

type Posting = {
  isLike: boolean;
  transaction: {
    id: string;
    identifier: string;
    description?: string | null;
    amount?: string | number | null;
    effectiveDate?: string | null;
    categoryObj?: {
      identifier: string;
      name?: string | null;
      color?: string | null;
    } | null;
  };
};

/** A row drawn while they load: a typical payment, each piece wrapped. */
const PLACEHOLDER_POSTING: Posting = {
  isLike: true,
  transaction: {
    id: "placeholder",
    identifier: "placeholder",
    description: "BLUE BOTTLE COFFEE 1180",
    amount: "-12.40",
    effectiveDate: "2026-09-01T00:00:00Z",
    categoryObj: { identifier: "placeholder", name: "Meals", color: null },
  },
};

/**
 * An account a payment would be posted to, opened from its suggestion - by its
 * View, or V: what the account has taken in the last six months, the payments
 * like this one first (paid to the same merchant, or described much as it is),
 * then the rest, the most recent first. Booked from here, by Book or ⌘↵, as
 * the footer's Book books the suggestion; Close, or Escape, leaves it.
 */
export function TriageAccountPostingsDialog({
  triageId,
  account,
  onClose,
  onBook,
}: {
  triageId: string;
  /** The account to open on; none, and it is shut. */
  account: TriagePostedAccount | null;
  onClose: () => void;
  onBook: () => void;
}) {
  return (
    <Dialog
      isOpen={account != null}
      onClose={onClose}
      isConstrained
      renderKey="triage-account-postings"
    >
      {account ? (
        <TriageAccountPostingsDialogContent
          triageId={triageId}
          account={account}
          onClose={onClose}
          onBook={onBook}
        />
      ) : (
        <></>
      )}
    </Dialog>
  );
}

function TriageAccountPostingsDialogContent({
  triageId,
  account,
  onClose,
  onBook,
}: {
  triageId: string;
  account: TriagePostedAccount;
  onClose: () => void;
  onBook: () => void;
}) {
  const { data, loading } = useBackendQuery(
    Queries.TRIAGE_ACCOUNT_POSTINGS_QUERY,
    {
      variables: { id: triageId, chartedAccountIdentifier: account.identifier },
      fetchPolicy: "cache-and-network",
    },
  );
  const node = data?.node;
  const postings: Posting[] =
    node && "accountPostings" in node ? node.accountPostings : [];
  const isLoading = loading && postings.length === 0;
  const shown = isLoading
    ? [PLACEHOLDER_POSTING, PLACEHOLDER_POSTING, PLACEHOLDER_POSTING]
    : postings;
  const like = shown.filter((posting) => posting.isLike);
  const rest = shown.filter((posting) => !posting.isLike);
  return (
    <div
      data-testid="triage-account-postings-dialog"
      className="flex flex-col min-h-0"
    >
      <div className="pl-5 pr-5 pt-5 pb-3">
        <Typography level="title-lg">Posted to {account.name}</Typography>
        <Typography level="body-sm" className="text-muted-foreground/80 mt-1">
          What it has taken in the last six months - the payments like this one
          first.
        </Typography>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto pl-5 pr-5 pb-4">
        {!isLoading && postings.length === 0 ? (
          <Typography
            level="body-md"
            className="text-muted-foreground/80 pt-8 pb-8 text-center"
          >
            Nothing posted here in the last six months.
          </Typography>
        ) : null}
        {[
          { title: "Like this one", rows: like, testId: "like" },
          { title: "Also posted here", rows: rest, testId: "rest" },
        ].map((section) =>
          section.rows.length > 0 ? (
            <div
              key={section.testId}
              data-testid={`triage-account-postings-${section.testId}`}
              className="mt-3"
            >
              <Typography
                level="body-xs"
                className="text-muted-foreground/80 uppercase [letter-spacing:0.04em] mb-1"
              >
                {section.title}
              </Typography>
              {section.rows.map((posting, index) => (
                <TriageAccountPosting
                  key={`${posting.transaction.id}-${index}`}
                  posting={posting}
                  isLoading={isLoading}
                />
              ))}
            </div>
          ) : null,
        )}
      </div>
      <div className="flex justify-end gap-2 p-3 [border-top-width:1px] [border-top-style:solid] border-border">
        <Button variant="outline" onClick={onClose}>
          Close
          <HotkeyDecorator hotkey="escape" color="inherit" />
        </Button>
        <Button
          data-testid="triage-account-postings-book"
          data-context-overlay-submit=""
          onClick={() => {
            onClose();
            onBook();
          }}
        >
          Book
          <HotkeyDecorator
            hotkey={{
              mac: "meta+enter",
              windows: "ctrl+enter",
              linux: "ctrl+enter",
            }}
            color="inherit"
          />
        </Button>
      </div>
    </div>
  );
}

/** One payment the account took: when, what it says, its category and what it came to. */
function TriageAccountPosting({
  posting,
  isLoading,
}: {
  posting: Posting;
  isLoading: boolean;
}) {
  const { transaction } = posting;
  const category = transaction.categoryObj;
  return (
    <div
      data-testid="triage-account-posting"
      className="grid grid-cols-[88px_minmax(0,1fr)_auto_auto] gap-x-3 items-center pt-1.5 pb-1.5 [border-bottom-width:1px] [border-bottom-style:solid] border-border"
    >
      <Typography level="body-sm" className="text-muted-foreground/80">
        <Skeleton loading={isLoading} variant="inline">
          {transaction.effectiveDate
            ? Utils.fDate(new Date(transaction.effectiveDate))
            : ""}
        </Skeleton>
      </Typography>
      <Typography level="body-sm" className="truncate">
        <Skeleton loading={isLoading} variant="inline">
          {transaction.description ?? ""}
        </Skeleton>
      </Typography>
      <div className="flex">
        {category?.name ? (
          <Skeleton loading={isLoading} variant="rectangular">
            <SimpleEnum
              label={category.name}
              enumColor={categoryEnumColor(category.color)}
              size="sm"
              variant="outlined"
            />
          </Skeleton>
        ) : null}
      </div>
      <Typography level="body-sm" className="text-right">
        <Skeleton loading={isLoading} variant="inline">
          {transaction.amount != null
            ? Utils.fCurrency(Number(transaction.amount), false, true)
            : ""}
        </Skeleton>
      </Typography>
    </div>
  );
}
